/**
 * RollbackModal Component
 *
 * Modal for rolling back a deployment with a reason.
 * Note: Cannot rollback the currently active deployment.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { rollbackFormSchema, type RollbackFormValidation } from '../../../lib/validators/deployment';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { useRollbackDeployment } from '../hooks/useRollbackDeployment';
import { getErrorMessage } from '../../../lib/utils/errors';

interface RollbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  deployment: DeploymentSanitized;
  onSuccess?: () => void;
}

/**
 * RollbackModal component
 */
export const RollbackModal: React.FC<RollbackModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  deployment,
  onSuccess,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RollbackFormValidation>({
    resolver: zodResolver(rollbackFormSchema),
    defaultValues: {
      reason: '',
    },
  });

  const rollbackMutation = useRollbackDeployment({
    tenantId,
    applicationId,
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    if (!rollbackMutation.isPending) {
      reset();
      rollbackMutation.reset();
      onClose();
    }
  };

  const onSubmit = (data: RollbackFormValidation) => {
    rollbackMutation.mutate({
      deploymentId: deployment.deploymentId,
      reason: data.reason,
    });
  };

  const isLoading = rollbackMutation.isPending;
  const error = rollbackMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Rollback Deployment</span>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action marks the deployment as rolled back!</p>
              <p className="text-sm">
                Rolling back a deployment flags it as problematic and records the reason.
                This helps track deployment issues and inform future deployment decisions.
              </p>
            </div>
          </Alert>

          {/* Deployment Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Deployment:</span>
              <p className="text-gray-900 font-mono text-sm">
                {deployment.deploymentId.slice(0, 8)}...
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Version:</span>
              <p className="text-gray-900">{deployment.version}</p>
            </div>
            {deployment.versionTag && (
              <div>
                <span className="text-sm font-medium text-gray-700">Version Tag:</span>
                <p className="text-gray-900">{deployment.versionTag}</p>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <p className="text-gray-900 capitalize">{deployment.deploymentStatus}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Deployed:</span>
              <p className="text-gray-900">
                {new Date(deployment.createdAt).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Reason Field */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
              Rollback Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              {...register('reason')}
              placeholder="Describe why this deployment is being rolled back..."
              disabled={isLoading}
              rows={3}
              className={`
                w-full px-3 py-2 border rounded-md shadow-sm text-sm
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                ${errors.reason ? 'border-red-300' : 'border-gray-300'}
                disabled:bg-gray-100 disabled:cursor-not-allowed
              `}
            />
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              This reason will be recorded and visible in the deployment history.
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            disabled={isLoading}
          >
            Rollback Deployment
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
